import { z } from 'zod';

/** Kostenstellen der Zeiterfassung (Phase 1). */
export const costCenterSchema = z.enum([
  'GESCHAEFTSLEITUNG',
  'TECHNIK',
  'LAGER',
  'MONTAGE',
  'SEKRETARIAT',
]);
export type CostCenter = z.infer<typeof costCenterSchema>;

/** Deutsche Beschriftungen der Kostenstellen (UI). */
export const COST_CENTER_LABELS: Record<CostCenter, string> = {
  GESCHAEFTSLEITUNG: 'Geschäftsleitung',
  TECHNIK: 'Technik',
  LAGER: 'Lager',
  MONTAGE: 'Montage',
  SEKRETARIAT: 'Sekretariat',
};

export const ttProjectStatusSchema = z.enum(['ACTIVE', 'ARCHIVED', 'COMPLETED']);
export type TtProjectStatus = z.infer<typeof ttProjectStatusSchema>;

// ── Unterkategorie (SubCategory) — eigenständig, admin-gepflegt ──
export const createTtSubCategorySchema = z.object({
  name: z.string().trim().min(1).max(160),
  isActive: z.boolean().default(true),
});
export type CreateTtSubCategoryInput = z.infer<typeof createTtSubCategorySchema>;

export const updateTtSubCategorySchema = createTtSubCategorySchema.partial();
export type UpdateTtSubCategoryInput = z.infer<typeof updateTtSubCategorySchema>;

export const ttSubCategoryResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type TtSubCategoryResponse = z.infer<typeof ttSubCategoryResponseSchema>;

// ── Zeiterfassungs-eigenes Projekt ──
// Zusätzlich zu den echten ProERP-Projekten anlegbar. Trägt eine Gesellschaft
// (LegalEntity aus den Einstellungen) und gibt individuell frei, welche
// Unterkategorien es nutzen darf. Optional mit einem ProERP-Projekt verknüpfbar (Hybrid).
export const createTtProjectSchema = z.object({
  name: z.string().trim().min(1).max(200),
  status: ttProjectStatusSchema.default('ACTIVE'),
  /** Gesellschaft (LegalEntity), zu der dieses Zeit-Projekt gehört. null = firmenübergreifend
   *  (z. B. Kategorie-Zeit-Projekte wie „Diverses"/„Pause"). */
  legalEntityId: z.string().uuid().nullable(),
  /** Unterkategorien, auf die dieses Projekt Zugriff hat. */
  subCategoryIds: z.array(z.string().uuid()).default([]),
  /** Optionale Verknüpfung mit dem zentralen ProERP-Projekt (Hybrid). */
  projectId: z.string().uuid().nullable().optional(),
});
export type CreateTtProjectInput = z.infer<typeof createTtProjectSchema>;

export const updateTtProjectSchema = createTtProjectSchema.partial();
export type UpdateTtProjectInput = z.infer<typeof updateTtProjectSchema>;

// ── Kategorie (übergeordnete Gliederungsebene, z. B. „Aufträge", „Vertrieb", „Technik") ──
// Schränkt ein, welche Projekte und Unterkategorien (Tätigkeiten) im Zeiteintrag
// wählbar sind. Unabhängig von der Kostenstelle (bleibt am Mitarbeiterstamm).
export const createTtCategorySchema = z.object({
  name: z.string().trim().min(1).max(160),
  sortOrder: z.number().int().default(0),
  /** true = zusätzlich zu den verknüpften Zeit-Projekten ist jedes echte ProERP-Projekt wählbar. */
  allowsAnyErpProject: z.boolean().default(false),
  isActive: z.boolean().default(true),
  /** Verknüpfte Zeit-Projekte (geteilter Pool — ein Zeit-Projekt kann mehreren Kategorien angehören). */
  ttProjectIds: z.array(z.string().uuid()).default([]),
  /** Verknüpfte Unterkategorien (Tätigkeiten), die in dieser Kategorie wählbar sind. */
  subCategoryIds: z.array(z.string().uuid()).default([]),
});
export type CreateTtCategoryInput = z.infer<typeof createTtCategorySchema>;

export const updateTtCategorySchema = createTtCategorySchema.partial();
export type UpdateTtCategoryInput = z.infer<typeof updateTtCategorySchema>;

export const ttCategoryResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  sortOrder: z.number().int(),
  allowsAnyErpProject: z.boolean(),
  isActive: z.boolean(),
  ttProjectIds: z.array(z.string().uuid()),
  subCategoryIds: z.array(z.string().uuid()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type TtCategoryResponse = z.infer<typeof ttCategoryResponseSchema>;

export const ttProjectResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  status: ttProjectStatusSchema,
  legalEntityId: z.string().uuid().nullable(),
  subCategoryIds: z.array(z.string().uuid()),
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type TtProjectResponse = z.infer<typeof ttProjectResponseSchema>;

// ── Firma (Gesellschaft) für die Timer-Auswahl ──
// Gesellschaften kommen aus den Einstellungen (LegalEntity), werden für die
// Zeiterfassung aber über einen eigenen, mit `timetracking.read` lesbaren Endpoint
// in minimaler Form bereitgestellt (nicht alle dürfen `settings.read`).
export const timerCompanySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isActive: z.boolean(),
});
export type TimerCompany = z.infer<typeof timerCompanySchema>;

// ── Timer-Projektliste (Hybrid) ──
// Im Timer wählbar: echte ProERP-Projekte ('project') und Zeiterfassungs-eigene
// Projekte ('tt'). Die Quelle steuert, welche Unterkategorien angeboten werden.
export const timerProjectKindSchema = z.enum(['project', 'tt']);
export type TimerProjectKind = z.infer<typeof timerProjectKindSchema>;

export const timerProjectSchema = z.object({
  kind: timerProjectKindSchema,
  id: z.string().uuid(),
  name: z.string(),
  /** Projekt-Code (echtes Projekt) bzw. Code der Hybrid-Verknüpfung (Zeit-Projekt). */
  code: z.string().nullable(),
});
export type TimerProject = z.infer<typeof timerProjectSchema>;

// ── Favoriten-Projekte (je Benutzer) ──
// Referenz auf ein Timer-Projekt (echtes oder Zeit-Projekt). Anzeige-Infos werden
// im Frontend gegen die geladene Projektliste aufgelöst.
export const timerFavoriteSchema = z.object({
  kind: timerProjectKindSchema,
  projectId: z.string().uuid(),
});
export type TimerFavorite = z.infer<typeof timerFavoriteSchema>;
